import logger from '../../utils/logger';
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { 
  Menu, 
  X, 
  Globe, 
  User, 
  LogOut,
  LayoutDashboard,
  ArrowUpCircle,
  Send,
  ArrowDownCircle,
  History,
  Settings,
  Users,
  FileText
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function Navbar() {
  const { t, language, toggleLanguage } = useLanguage();
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [headerPages, setHeaderPages] = useState([]);
  const [joinUsTitle, setJoinUsTitle] = useState({ fr: 'Rejoignez-nous', en: 'Join Us' });

  // Check if admin is impersonating a user - hide wallet links in header
  const isImpersonating = localStorage.getItem('payhub_impersonating') === 'true';

  // Fetch pages that should appear in header and join us content
  useEffect(() => {
    const fetchHeaderPages = async () => {
      try {
        const response = await fetch(`${API_URL}/api/public/pages`);
        if (response.ok) {
          const data = await response.json();
          // Filter pages that should show in header
          const pagesForHeader = (data.pages || []).filter(p => p.show_in_header === true);
          setHeaderPages(pagesForHeader);
        }
      } catch (error) {
        logger.error('Error fetching header pages:', error);
      }
    };
    
    const fetchJoinUsContent = async () => {
      try {
        const response = await fetch(`${API_URL}/api/public/join-us-content`);
        if (response.ok) {
          const data = await response.json();
          if (data.nav_title) {
            setJoinUsTitle(data.nav_title);
          }
        }
      } catch (error) {
        logger.error('Error fetching join us content:', error);
      }
    };
    
    fetchHeaderPages();
    fetchJoinUsContent();
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const isActive = (path) => location.pathname === path;

  // In impersonation mode: hide ALL wallet links from header
  // Admin uses Quick Actions in the Dashboard instead
  const navLinks = user && !isImpersonating ? [
    { path: '/dashboard', label: t('nav.dashboard'), icon: LayoutDashboard },
    { path: '/topup', label: t('nav.topup'), icon: ArrowUpCircle },
    { path: '/transfer', label: t('nav.transfer'), icon: Send },
    { path: '/withdraw', label: t('nav.withdraw'), icon: ArrowDownCircle },
    { path: '/transactions', label: t('nav.transactions'), icon: History },
  ] : [];

  return (
    <nav className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-4">
            <Link to={user ? '/dashboard' : '/'} className="flex items-center gap-2" data-testid="logo-link">
              <img 
                src="/quebec-plus-icon.png" 
                alt="Q+" 
                className="h-9 w-9 rounded-xl object-contain"
              />
              <span className="font-bold text-xl hidden sm:block">PayHub</span>
            </Link>
            
            {/* Quebec+ Badge */}
            <div className="hidden lg:flex flex-col items-start px-3 py-1.5 rounded-xl bg-[#003DA5]/10 border border-[#003DA5]/20">
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-semibold text-[#003DA5]">
                  ⚜️ PayHub Québec+
                </span>
              </div>
              <span className="text-[10px] text-[#003DA5]/80 mt-0.5">
                {language === 'fr' ? 'Soutenons les monnaies locales du Québec!' : "Supporting Quebec's local currencies!"}
              </span>
            </div>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {/* Join Us link - always visible */}
            <Link
              to="/rejoignez-nous"
              data-testid="nav-join-us"
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                isActive('/rejoignez-nous')
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              {joinUsTitle[language] || joinUsTitle.fr || 'Rejoignez-nous'}
            </Link>
            
            {/* Dynamic CMS pages with show_in_header */}
            {headerPages.map((page) => {
              // Handle both old format (title as string) and new format (title_fr/title_en or title as object)
              let pageTitle;
              if (typeof page.title === 'object' && page.title !== null) {
                pageTitle = language === 'fr' ? (page.title.fr || page.title.en) : (page.title.en || page.title.fr);
              } else {
                pageTitle = language === 'fr' ? (page.title_fr || page.title) : (page.title_en || page.title);
              }
              
              return (
                <Link
                  key={page.page_id}
                  to={`/page/${page.slug}`}
                  data-testid={`nav-page-${page.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    location.pathname === `/page/${page.slug}`
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                >
                  {pageTitle}
                </Link>
              );
            })}
            
            {navLinks.map(({ path, label }) => (
              <Link
                key={path}
                to={path}
                data-testid={`nav-${path.slice(1)}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  isActive(path)
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                {label}
              </Link>
            ))}
          </div>

          {/* Right side */}
          <div className="flex items-center gap-2">
            {/* Language Toggle - Shows alternative language highlighted */}
            <button
              onClick={toggleLanguage}
              data-testid="language-toggle"
              className="flex items-center rounded-full border border-[#003DA5]/30 overflow-hidden"
            >
              <span 
                className={`px-3 py-1.5 text-sm font-semibold transition-all ${
                  language === 'en' 
                    ? 'bg-[#003DA5] text-white' 
                    : 'bg-transparent text-muted-foreground'
                }`}
              >
                FR
              </span>
              <span 
                className={`px-3 py-1.5 text-sm font-semibold transition-all ${
                  language === 'fr' 
                    ? 'bg-[#003DA5] text-white' 
                    : 'bg-transparent text-muted-foreground'
                }`}
              >
                EN
              </span>
            </button>

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="rounded-full" data-testid="user-menu-trigger">
                    {user.picture ? (
                      <img src={user.picture} alt={user.name} className="h-8 w-8 rounded-full" />
                    ) : (
                      <User className="h-5 w-5" />
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{user.name}</p>
                    <p className="text-xs text-muted-foreground">{user.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/settings" className="cursor-pointer" data-testid="settings-link">
                      <Settings className="mr-2 h-4 w-4" />
                      {t('nav.settings')}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-destructive" data-testid="logout-btn">
                    <LogOut className="mr-2 h-4 w-4" />
                    {t('nav.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/login">
                  <Button variant="ghost" className="rounded-full" data-testid="login-btn">
                    {t('nav.login')}
                  </Button>
                </Link>
                <Link to="/register" className="hidden sm:block">
                  <Button className="rounded-full" data-testid="register-btn">
                    {t('nav.register')}
                  </Button>
                </Link>
              </div>
            )}

            {/* Mobile menu button */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden rounded-full"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-border animate-fade-in">
            <div className="flex flex-col gap-1">
              {/* Join Us link - always visible in mobile */}
              <Link
                to="/rejoignez-nous"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-nav-join-us"
                className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                  isActive('/rejoignez-nous')
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                <Users className="h-5 w-5" />
                {joinUsTitle[language] || joinUsTitle.fr || 'Rejoignez-nous'}
              </Link>
              
              {/* Dynamic CMS pages with show_in_header - mobile */}
              {headerPages.map((page) => {
                // Handle both old format (title as string) and new format (title_fr/title_en or title as object)
                let pageTitle;
                if (typeof page.title === 'object' && page.title !== null) {
                  pageTitle = language === 'fr' ? (page.title.fr || page.title.en) : (page.title.en || page.title.fr);
                } else {
                  pageTitle = language === 'fr' ? (page.title_fr || page.title) : (page.title_en || page.title);
                }
                
                return (
                  <Link
                    key={page.page_id}
                    to={`/page/${page.slug}`}
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid={`mobile-nav-page-${page.slug}`}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                      location.pathname === `/page/${page.slug}`
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                  >
                    <FileText className="h-5 w-5" />
                    {pageTitle}
                  </Link>
                );
              })}
              
              {user && navLinks.map(({ path, label, icon: Icon }) => (
                <Link
                  key={path}
                  to={path}
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid={`mobile-nav-${path.slice(1)}`}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                    isActive(path)
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  {label}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
